export const MONTHS = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
] as const

/** Month name for a 1-based month number (1 = janeiro). */
export function monthName(month: number): string {
  return MONTHS[month - 1] ?? ''
}

/** BRL as in the handoff: `R$ 184.320,00`. */
export function formatBRL(value: number): string {
  const abs = Math.abs(value).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return `${value < 0 ? '-' : ''}R$ ${abs}`
}

/** Date-only strings (`2026-09-12`) are calendar dates: parse them in local time, not UTC. */
function toDate(iso: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(iso)
}

/** "d mmm", e.g. `12 set`. */
export function fmtDate(iso: string): string {
  const d = toDate(iso)
  return `${d.getDate()} ${MONTHS[d.getMonth()]?.slice(0, 3)}`
}

/** "HH:mm" in local time, e.g. `19:00`. */
export function fmtTime(iso: string): string {
  const d = new Date(iso)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/** Human-readable file size: `188 KB`, `1,2 MB`. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`
}

/** "mmm yyyy", e.g. `abr 2023`. */
export function fmtMonthYear(iso: string): string {
  const d = toDate(iso)
  return `${MONTHS[d.getMonth()]?.slice(0, 3)} ${d.getFullYear()}`
}

/** Relative time: `agora`, `há 5h`, `há 3d`. `now` is injectable for tests. */
export function ago(iso: string, now: number = Date.now()): string {
  const hours = Math.round((now - Date.parse(iso)) / 36e5)
  if (hours < 1) return 'agora'
  if (hours < 24) return `há ${hours}h`
  return `há ${Math.round(hours / 24)}d`
}

/** Initials from the first and second word of a name. */
export function initialsOf(name: string): string {
  const [a = '', b = ''] = name.trim().split(/\s+/)
  return ((a[0] ?? '') + (b[0] ?? '')).toUpperCase()
}

export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? ''
}

/**
 * Parses a pt-BR money input (`184.320,00`, `R$ 1.500,5`, `250`) into a number.
 * `.` is the thousands separator and `,` the decimal one. Returns null for anything else
 * (empty, letters, negatives, more than two decimals, several commas).
 */
export function parseBRL(input: string): number | null {
  const cleaned = input.replace(/R\$|\s/g, '')
  if (!/^\d+(\.\d{3})*(,\d{1,2})?$|^\d+(,\d{1,2})?$/.test(cleaned)) return null
  return Number(cleaned.replace(/\./g, '').replace(',', '.'))
}

/** Number to the plain pt-BR input form, without the currency symbol: `184.320,00`. */
export function formatDecimalBR(value: number): string {
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
